import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { ExporPiListSortField } from "@saleor/createOrderExport/urls";
import { ExportPisQuery, useGetCreditLimitLazyQuery } from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { ListProps, RelayToFlat, SortPage } from "@saleor/types";
import { countryMapping } from "@saleor/utils/countryMapping";
import { getArrowDirection } from "@saleor/utils/sort";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import CustomerBlockDialog from "../CustomerBlockDialog";

interface SoldToListProps extends ListProps, SortPage<ExporPiListSortField> {
  data: RelayToFlat<ExportPisQuery["exportPis"]>;
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  onViewContract: (id) => void;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      overflowX: "auto",
      width: "100%",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderBottom: 0,
      borderRadius: "2px",
      "& .MuiTableCell-head": {
        "& *": {
          color: theme.palette.grey[600]
        }
      }
    },
    cursorPointer: {
      cursor: "pointer"
    },
    link: {
      cursor: "pointer"
    },
    colPiNumber: {
      "& .MuiTypography-root": {
        color: theme.palette.info.main
      }
    },
    colPoNumber: {
      color: theme.palette.grey[800]
    },
    colSoldTo: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[900]
    },
    colShipToName: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[900]
    },
    colShipToCountry: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[800]
    },
    colIncotermCode: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[900]
    },
    colPaymentTerms: {
      whiteSpace: "nowrap",
      color: theme.palette.grey[900]
    },
    pagination: {
      background: "rgba(212, 244, 248, 0.8)"
    },
    paginateFooter: {
      background: "rgba(212, 244, 248, 0.8)",
      padding: "12px 20px",
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      borderLeft: `1px solid ${theme.palette.grey[300]}`
    },
    rowClickable: {
      cursor: "pointer"
    }
  }),
  { name: "SoldToList" }
);

const SoldToList: React.FC<SoldToListProps> = props => {
  const {
    data,
    disabled,
    settings,
    pageInfo,
    totalCount,
    latestPageItemNumber,
    sort,
    onSort,
    onPreviousPage,
    onNextPage,
    onUpdateListSettings,
    onNextLastPage,
    onPreviousFirstPage,
    onViewContract
  } = props;
  const intl = useIntl();
  const classes = useStyles();
  const [onOpenCustomerBlock, setOpenCustomerBlock] = useState<boolean>(false);
  const [itemSelected, setItemSelected] = useState(null);
  const [
    getCreditLimitLazy
    // { data: creditLimitData, loading: creditLimitDataLoading }
  ] = useGetCreditLimitLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted(data) {
      if (data.getCreditLimit.creditBlockStatus) {
        setOpenCustomerBlock(true);
      } else {
        onViewContract(itemSelected.id);
      }
    }
  });

  return (
    <>
      <div className={classes.root}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader
                className={classes.colPiNumber}
                direction={
                  sort.sort === ExporPiListSortField.piNo
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(ExporPiListSortField.piNo)}
              >
                {intl.formatMessage({ defaultMessage: "PI No." })}
              </TableCellHeader>
              <TableCellHeader className={classes.colPoNumber}>
                {intl.formatMessage({ defaultMessage: "PO No." })}
              </TableCellHeader>
              <TableCellHeader className={classes.colSoldTo}>
                {intl.formatMessage({ defaultMessage: "Sold to" })}
              </TableCellHeader>
              <TableCellHeader className={classes.colShipToName}>
                {intl.formatMessage({ defaultMessage: "Ship to" })}
              </TableCellHeader>
              <TableCellHeader className={classes.colShipToCountry}>
                {intl.formatMessage({ defaultMessage: "Ship to Country" })}
              </TableCellHeader>
              <TableCellHeader className={classes.colIncotermCode}>
                {intl.formatMessage({ defaultMessage: "Incoterm Code" })}
              </TableCellHeader>
              <TableCellHeader className={classes.colPaymentTerms}>
                {intl.formatMessage({ defaultMessage: "Payment Terms" })}
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderCollection(data, (item, index) => (
              <TableRow
                className={classes.rowClickable}
                key={index}
                onClick={() => {
                  setItemSelected(item);
                  getCreditLimitLazy({
                    variables: {
                      input: {
                        contractNo: item.code
                      }
                    }
                  });
                }}
                data-testid={`item-row-${index}`}
              >
                <TableCell className={classes.colPiNumber}>
                  <MyTypography variant="subtitle3">
                    {item ? item.code : <Skeleton />}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.colPoNumber}>
                  {item ? item.poNo : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colSoldTo}>
                  {item ? item.soldTo?.displayText : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colShipToName}>
                  {item ? item.shipToName : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colShipToCountry}>
                  {item ? countryMapping(item.shipToCountry) : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colIncotermCode}>
                  {item ? item.incoterm : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colPaymentTerms}>
                  {item ? item.paymentTermKey : <Skeleton />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {/* <TableFooter>
          <TableRow>
            <TableCell className={classes.pagination}>
              <TablePaginationCustom
                settings={settings}
                hasNextPage={
                  pageInfo && !disabled ? pageInfo.hasNextPage : false
                }
                onNextPage={onNextPage}
                onPreviousFirstPage={onPreviousFirstPage}
                onNextLastPage={onNextLastPage}
                onUpdateListSettings={onUpdateListSettings}
                hasPreviousPage={
                  pageInfo && !disabled ? pageInfo.hasPreviousPage : false
                }
                onPreviousPage={onPreviousPage}
                latestPageItemNumber={latestPageItemNumber}
                totalCount={totalCount}
                isBackFistPage
                isNextLastPage
                isShowIndex
              />
            </TableCell>
          </TableRow>
        </TableFooter> */}
        </Table>
      </div>
      <Box className={classes.paginateFooter}>
        <TablePaginationCustom
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount}
          isBackFistPage
          isNextLastPage
          isShowIndex
          disabled={disabled}
        />
      </Box>
      <CustomerBlockDialog
        content={`ไม่สามารถทำรายการได้ เนื่องจากลูกค้าติด \n (Credit Block) กรุณาติดต่อเจ้าหน้าที่สินเชื่อ`}
        open={onOpenCustomerBlock}
        onClose={() => {
          setOpenCustomerBlock(false);
        }}
      />
    </>
  );
};

export default SoldToList;
